// Apolo IDE — SidePanelDrawer (Sprint v6 B6).
//
// Responsive wrapper for the side panel:
// - xl+ (1280+): rendered inline as a split panel (returns children directly).
// - md..xl  (768..1279): overlay drawer that slides in from the right.
// - < md   (<768): full-screen sheet from the right.
//
// Caller still controls `open` + `onClose`. Backdrop click closes.
// Spring physics match the project standard (stiffness 320, damping 28).

import { motion, AnimatePresence } from 'framer-motion';
import { useBreakpoint } from '../../../hooks/useBreakpoint.js';

export default function SidePanelDrawer({ open, onClose, children }) {
  const bp = useBreakpoint();
  const isLarge = bp === 'xl' || bp === '2xl';

  if (isLarge) {
    // Split-panel mode: side panel always visible when `open`.
    if (!open) return null;
    return (
      <aside
        role="complementary"
        aria-label="Painel lateral"
        className="flex w-[420px] shrink-0 flex-col border-l border-apolo-borderSubtle bg-apolo-surface1"
      >
        {children}
      </aside>
    );
  }

  const isFullscreen = bp === 'xs' || bp === 'sm';

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            aria-hidden="true"
            className="fixed inset-0 z-40 bg-black/60"
          />
          <motion.aside
            key="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Painel lateral"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className={
              isFullscreen
                ? 'fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-apolo-surface1 border-l border-apolo-borderSubtle shadow-2xl pb-[env(safe-area-inset-bottom)]'
                : 'fixed inset-y-0 right-0 z-50 flex w-[420px] flex-col bg-apolo-surface1 border-l border-apolo-borderSubtle shadow-2xl pb-[env(safe-area-inset-bottom)]'
            }
          >
            {children}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
